import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, Mail, Phone, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ensureOneSignalLogin } from '../utils/oneSignalInit';
import Layout from '../components/Layout';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useApp();

  // 👉 Redirección automática si ya hay sesión activa
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [useEmail, setUseEmail] = useState(false);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.identifier.trim()) {
      newErrors.identifier = useEmail
        ? 'Correo es obligatorio.'
        : 'Teléfono es obligatorio.';
    }
    if (!formData.password) {
      newErrors.password = 'La contraseña es obligatoria.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const loggedUser = await login(
        formData.identifier,
        formData.password
      );

      if (!loggedUser) {
        setMessage('Credenciales incorrectas.');
        setMessageType('error');
        setLoading(false);
        return;
      }

      // 👉 ¡AQUÍ ESTÁ EL AJUSTE DE ONESIGNAL!
      // Vincula este navegador con el ID del usuario en tu base de datos
      if (window.OneSignal) {
        ensureOneSignalLogin(loggedUser.id, { requirePermission: true }).catch((err) =>
          console.warn('OneSignal login:', err)
        );
      }

      setMessage(`¡Bienvenido, ${loggedUser.first_name}!`);
      setMessageType('success');

      setTimeout(() => {
        navigate('/home');
      }, 1200);

    } catch (err) {
      console.error(err);
      setMessage('Error al iniciar sesión.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showNavigation={false}>
      {/* LayoutGroup envuelve la página para permitir la animación compartida */}
      <LayoutGroup>
        <div className="min-h-screen bg-[#f4f7fb] flex flex-col px-5 py-6 md:py-8 relative overflow-hidden">
          
          {/* Decoración de fondo sutil */}
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-900/5 to-transparent pointer-events-none"></div>

          {/* HEADER / BOTÓN VOLVER */}
          <motion.div
            className="relative z-10 flex items-center mb-4 max-w-md mx-auto w-full"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors shadow-sm active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </motion.div>

          {/* CONTENEDOR PRINCIPAL */}
          <motion.div
            className="relative z-10 w-full max-w-md mx-auto flex-1 flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            
            {/* =========================================
                LOGO ANIMADO COMPARTIDO (Tamaño idéntico a Welcome)
                ========================================= */}
            <div className="text-center mb-6 flex flex-col items-center">
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 md:px-8 md:py-5 rounded-[28px] md:rounded-[32px] shadow-[0_15px_30px_rgba(30,58,138,0.25)] flex items-center gap-4 md:gap-5">
                
                {/* 🔥 EL LOGO ICONO QUE VIAJA ENTRE PÁGINAS */}
                <motion.img
                  layoutId="logo-mark"
                  src="/logo-icono.png" 
                  alt="Logo Icono"
                  className="w-20 h-20 md:w-20 md:h-20 object-contain rounded-xl"
                />
                
                {/* TEXTO HTML DEL LOGO */}
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="flex flex-col text-left"
                >
                  <span className="text-4xl md:text-5xl font-black tracking-tight leading-none text-white pb-0.5">
                    Remesas
                  </span>
                  <span className="text-4xl md:text-5xl font-black tracking-tight leading-none text-blue-200">
                    Venezuela
                  </span>
                </motion.div>
              </div>

              {/* TEXTO INSTRUCTIVO */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-slate-500 font-medium mt-4 text-sm"
              >
                Accede a tu cuenta para continuar
              </motion.p>
            </div>

            {/* TARJETA DEL FORMULARIO */}
            <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 p-6 md:p-8">
              
              {/* MENSAJES DE ALERTA */}
              <AnimatePresence>
                {message && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-6"
                  >
                    <div className={`flex items-center gap-3 p-4 rounded-2xl border ${
                      messageType === 'success'
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      {messageType === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                      <p className="text-sm font-bold tracking-tight">{message}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* FORMULARIO */}
              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* INPUT: IDENTIFICADOR (Teléfono / Email) */}
                <div className="relative w-full">
                  <AnimatePresence mode="wait">
                    {!useEmail ? (
                      <motion.div
                        key="phone"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="relative"
                      >
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">
                          Teléfono
                        </label>
                        <PhoneInput
                          country="co"
                          value={formData.identifier}
                          onChange={(phone) =>
                            setFormData({
                              ...formData,
                              identifier: phone.startsWith('+') ? phone : `+${phone}`,
                            })
                          }
                          inputStyle={{
                            width: '100%',
                            borderRadius: '16px',
                            height: '56px',
                            paddingLeft: '60px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#f8fafc',
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#1e293b'
                          }}
                          buttonStyle={{ 
                            borderRadius: '16px 0 0 16px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: 'transparent',
                            paddingLeft: '8px'
                          }}
                          enableSearch
                          placeholder="Ingresa tu número"
                          disabled={loading}
                        />

                        <button
                          type="button"
                          onClick={() => setUseEmail(true)}
                          className="absolute right-2 top-[34px] bg-blue-50 hover:bg-blue-100 text-blue-600 p-2.5 rounded-xl transition-colors"
                          title="Usar correo electrónico"
                        >
                          <Mail className="w-4 h-4" />
                        </button>

                        {errors.identifier && (
                          <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">
                            {errors.identifier}
                          </p>
                        )}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="email"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="relative"
                      >
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">
                          Correo Electrónico
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="email"
                            placeholder="tu@correo.com"
                            value={formData.identifier}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                identifier: e.target.value,
                              })
                            }
                            className="w-full pl-12 pr-14 py-4 h-[56px] bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all font-semibold text-slate-800 placeholder:font-medium placeholder:text-slate-400"
                            disabled={loading}
                          />

                          <button
                            type="button"
                            onClick={() => setUseEmail(false)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-50 hover:bg-blue-100 text-blue-600 p-2.5 rounded-xl transition-colors"
                            title="Usar número de teléfono"
                          >
                            <Phone className="w-4 h-4" />
                          </button>
                        </div>

                        {errors.identifier && (
                          <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">
                            {errors.identifier}
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* INPUT: CONTRASEÑA */}
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 ml-1">
                      Contraseña
                    </label>
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                  
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className="w-full pl-12 pr-14 py-4 h-[56px] bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all font-semibold text-slate-800 placeholder:text-slate-400 tracking-wide"
                      disabled={loading}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {errors.password && (
                    <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* BOTÓN DE ENVIAR */}
                <motion.button
                  type="submit"
                  className={`w-full py-4 mt-4 rounded-2xl font-black text-white text-[15px] uppercase tracking-wider transition-all duration-300 shadow-[0_10px_20px_rgba(30,58,138,0.15)] hover:shadow-[0_15px_25px_rgba(30,58,138,0.25)] active:scale-[0.98] ${
                    loading ? 'bg-blue-800 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Conectando...
                    </div>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </motion.button>
              </form>

              {/* SECCIÓN REGISTRO */}
              <div className="mt-8 text-center pt-6 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-500">
                  ¿Aún no tienes cuenta?{' '}
                  <button 
                    onClick={() => navigate('/register')}
                    className="font-bold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Regístrate aquí
                  </button>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </LayoutGroup>
    </Layout>
  );
};

export default Login;